import { useEffect, useState } from 'react';
import { auth } from '@/api/auth';
import { ApiError } from '@/api/client';
import type { User } from '@/types';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

// Module-level promise dedupes the /me call across components mounted at once.
let inflight: Promise<User | null> | null = null;

async function fetchMe(): Promise<User | null> {
  if (!inflight) {
    inflight = auth
      .me()
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          return null;
        }
        throw err;
      })
      .finally(() => {
        // Allow a re-fetch after the first call resolves; we just want to
        // dedupe near-simultaneous initial mounts.
        setTimeout(() => {
          inflight = null;
        }, 0);
      });
  }
  return inflight;
}

export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((user) => {
        if (cancelled) return;
        setState({ user, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'auth check failed';
        setState({ user: null, loading: false, error: msg });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
